import {ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ConfirmationService, MenuItem, MessageService, SelectItem} from 'primeng/api';
import {
    ConfiguratorDatastoreService,
    ConfiguratorEtlService,
    ConfiguratorTableService,
    DatastoreDto,
    EngineEtlService,
    EtlConf,
    EtlDuplicateDTO,
    FileProcessingLoadConf,
    SharedFileExtractConf,
    TaskDTO
} from '@tj/tj-dmp-lib-api';
import {HttpErrorResponse, HttpEvent, HttpEventType} from '@angular/common/http';
import {Subscription} from 'rxjs';
import {ImportStep} from '../../../components/import-steps/import-steps.component';
import {UntypedFormBuilder} from '@angular/forms';
import {TasksService} from '../../../components/tasks/tasks.service';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Router} from '@angular/router';
import {AccountService} from '../../../auth/account.service';
import {isTenantAdmin} from '../../../auth/admin-levels';
import { PagePermissionsService } from '../../../auth/page-permissions.service';

@Component({
    selector: 'dmp-etl',
    templateUrl: './etl.component.html',
    styleUrls: ['./etl.component.scss'],
    providers: [MessageService],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class EtlComponent implements OnInit {
    public directionMode: 'IMPORT' | 'EXPORT';
    public innerPath: string;

    constructor(
        protected configuratorEtlService: ConfiguratorEtlService,
        protected datastoreConfiguratorService: ConfiguratorDatastoreService,
        protected tableResourceService: ConfiguratorTableService,
        protected fb: UntypedFormBuilder,
        protected engineEtlService: EngineEtlService,
        protected tasksService: TasksService,
        protected messageService: MessageService,
        protected confirmationService: ConfirmationService,
        protected translate: TranslateService,
        protected router: Router,
        protected readonly route: ActivatedRoute,
        protected readonly accountService: AccountService,
        protected pagePermissions : PagePermissionsService
    ) {
        this.translate.onLangChange.subscribe(() => this.updateHeader());
    }

    breadcrumb: MenuItem[] = [
        { label: this.translate.instant('menu.flow.label') },
        { label: this.translate.instant('menu.flow.imports') }
    ];
    description = this.translate.instant('etl.list.descriptionImports');
    datastore: string = null;
    partner: string = null;
    accessiblePartners: string[] = [];
    possiblePartners: string[] = [];
    etls: EtlConf[];
    currentEtl: EtlConf = null;
    selectedEtls: EtlConf[] = [];
    json: string[] = null;
    isSuperAdmin = false;
    isAuthorizedAdmin = false;
    initialPartner: string = null;
    selectedPartner: string = null;

    @ViewChild('dataFileInput', { static: true })
    dataFileInput: ElementRef<HTMLInputElement>;

    @ViewChild('etlFileInput')
    etlFileInput: ElementRef<HTMLInputElement>;

    @ViewChild('menu') menu;

    form = this.fb.group({
        file: [],
        fileContentType: [],
    });
    file: any = null;
    uploadFile = false;
    /** Part du fichier transmise, en %. `null` tant que le navigateur n'a rien rapporté. */
    uploadProgress: number | null = null;
    /**
     * Fil des étapes montré pendant le transfert. La suite (lecture du fichier, import) se déroule
     * sur l'écran de tâche, qui reprend le même fil : le parcours se lit d'un écran à l'autre.
     */
    uploadSteps: ImportStep[] = this.initialUploadSteps();

    private initialUploadSteps(): ImportStep[] {
        return [
            { id: 'UPLOAD', label: this.translate.instant('etl.list.uploadStepTransfer'), state: 'current' },
            { id: 'EXTRACTION', label: this.translate.instant('etl.list.uploadStepRead'), state: 'todo' },
            { id: 'LOAD', label: this.translate.instant('etl.list.uploadStepImport'), state: 'todo' }
        ];
    }
    private uploadSubscription: Subscription | null = null;

    actionsList = [];
    itemsList: any[];

    copyDialog = false;
    // Liés par [(ngModel)] dans la modale de duplication ; ils n'étaient pas déclarés et
    // n'existaient donc qu'à l'exécution, créés par Angular sur l'instance.
    etlName: string = null;
    etlDescription: string = null;
    selectedEtlId: string = null;

    ngOnInit() {
        this.buildItemsList();
        this.loadAll();
        this.directionMode = this.route.snapshot.routeConfig.path.includes('import') ? 'IMPORT' : 'EXPORT';
        this.innerPath = this.directionMode === 'IMPORT' ? 'import-v1' : 'export';
        this.updateHeader();
    }

    private updateHeader(): void {
        if (!this.directionMode) {
            return;
        }
        this.breadcrumb = [{ label: this.translate.instant('menu.flow.label') }, { label: this.directionMode === 'IMPORT' ? this.translate.instant('menu.flow.imports') : this.translate.instant('menu.flow.exports') }];
        this.description = this.translate.instant(this.directionMode === 'IMPORT' ? 'etl.list.descriptionImports' : 'etl.list.descriptionExports');
    }

    datastoreChange(datastore: DatastoreDto = null) {
        if (datastore == null) {
            this.accessiblePartners = [];
            this.datastore = null;
            this.loadAll();
        } else {
            this.datastoreConfiguratorService.getDatastoreAccessiblePartnersUsingGET(datastore.id).subscribe((partners) => {
                this.accessiblePartners = partners;
                this.datastore = datastore ? datastore.code : null;
                this.loadAll();
            });
        }
    }

    partnerChange(partner: SelectItem = null) {
        this.partner = partner ? partner.value : null;
        this.loadAll();
    }

    private loadAll() {
        this.accountService.fetch().subscribe(account => {
            this.isSuperAdmin = account.admin ? true : false;
            this.isAuthorizedAdmin = isTenantAdmin(account, this.datastore);
            if (this.datastore) {
                this.configuratorEtlService
                    .getAllEtls(this.datastore, [this.directionMode])
                    .subscribe((etls) => {
                        if(this.isSuperAdmin || this.isAuthorizedAdmin){
                            this.etls = etls;
                        } else {
                            this.etls = etls.filter(etl => 
                            this.accessiblePartners?.includes(etl?.owner) || this.accessiblePartners?.some(p => etl?.affectedPartners?.includes(p)));
                        }
                    });
            } else {
                this.etls = [];
            }
        });
    }

    runEtl(etl: EtlConf) {
        this.currentEtl = etl;
        // this.fileInput.nativeElement.click();
    }

    setFileData(event: Event, field: string, isImage: boolean): void {
        //
        if (
            event &&
            event.target &&
            (event.target as any).files &&
            (event.target as any).files[0]
        ) {
            const file = (event.target as any).files[0];
            this.file = file;
            this.previewEtl();
        }
    }

    /**
     * Transmet le fichier et ouvre la tâche créée.
     *
     * La fenêtre de transfert est modale et sans croix : sans gestion d'erreur, un transfert
     * interrompu (coupure réseau, proxy, serveur redémarré) la laissait affichée indéfiniment, sans
     * aucun message. L'avancement est donc affiché, un échec la ferme avec un message, et
     * l'utilisateur peut annuler.
     */
    previewEtl() {
        this.uploadFile = true;
        this.uploadProgress = null;
        this.uploadSubscription = this.engineEtlService.previewWithFileUsingPOST(
            this.datastore,
            'null',
            this.currentEtl.id,
            null,
            this.file,
            'events',
            true)
            .subscribe({
                next: (event: HttpEvent<TaskDTO>) => {
                    if (event.type === HttpEventType.UploadProgress && event.total) {
                        this.uploadProgress = Math.round((event.loaded / event.total) * 100);
                        if (this.uploadProgress === 100) {
                            // Le serveur a tout reçu : il ouvre la tâche et commence à lire le fichier.
                            this.uploadSteps = [
                                { ...this.uploadSteps[0], state: 'done' },
                                { ...this.uploadSteps[1], state: 'current' },
                                this.uploadSteps[2]
                            ];
                        }
                    } else if (event.type === HttpEventType.Response) {
                        const task = event.body;
                        this.endUpload();
                        if (!task?.id) {
                            // Réponse sans tâche : rien à ouvrir, et l'utilisateur doit le savoir.
                            this.messageService.add({
                                severity: 'error',
                                summary: this.translate.instant('etl.list.transferImpossibleSummary'),
                                detail: this.translate.instant('etl.list.noTaskCreatedDetail')
                            });
                            return;
                        }
                        this.router.navigate(['/h/flux/import-v1/task', task.tenant, task.id]);
                    }
                },
                error: (err: HttpErrorResponse) => {
                    this.endUpload();
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('etl.list.transferImpossibleSummary'),
                        detail: this.uploadErrorDetail(err),
                        life: 10000
                    });
                }
            });
    }

    /**
     * Message d'échec de transfert. Le serveur répond « Failed to parse multipart servlet request »
     * quand le fichier lui parvient de façon incomplète ou trop lente (il abandonne la lecture) :
     * aucune tâche n'est alors créée, ce que l'utilisateur doit comprendre sans lire les logs.
     */
    private uploadErrorDetail(err: HttpErrorResponse): string {
        const serverDetail = err?.error?.detail || err?.error?.title;
        if (err?.status === 0) {
            return this.translate.instant('etl.list.connectionInterruptedDetail');
        }
        if (err?.status === 400 && /multipart/i.test(String(serverDetail))) {
            return this.translate.instant('etl.list.incompleteFileDetail');
        }
        if (err?.status === 413) {
            return this.translate.instant('etl.list.fileTooLargeDetail');
        }
        return serverDetail || err?.message || this.translate.instant('etl.list.transferFailedDetail');
    }

    cancelUpload() {
        // Branchée aussi sur `(onHide)` du p-dialog pour que la croix annule le transfert comme le
        // bouton. `endUpload()` remet `uploadSubscription` à null : la garde évite donc d'annoncer
        // « Transfert annulé » quand la modale se ferme parce que le transfert vient d'aboutir ou
        // d'échouer.
        if (!this.uploadSubscription) {
            return;
        }
        this.uploadSubscription.unsubscribe();
        this.endUpload();
        this.messageService.add({ severity: 'info', summary: this.translate.instant('etl.list.transferCancelledSummary') });
    }

    /**
     * Annulation de la duplication de job, partagée entre le bouton « Annuler » et la croix.
     */
    cancelCopy() {
        this.copyDialog = false;
        this.etlName = null;
        this.etlDescription = null;
        this.selectedPartner = null;
    }

    private endUpload() {
        this.uploadFile = false;
        this.uploadProgress = null;
        this.uploadSteps = this.initialUploadSteps();
        this.uploadSubscription = null;
        // Sans remise à zéro, choisir à nouveau le même fichier ne déclenche pas `change`.
        if (this.dataFileInput?.nativeElement) {
            this.dataFileInput.nativeElement.value = '';
        }
    }

    // DEAD CODE (2026-09) : jamais appelée depuis le template de cet écran. L'import manuel réel
    // passe par previewEtl() -> previewWithFileUsingPOST, puis l'écran de tâche (bouton "Importer").
    sendEtl() {

        this.engineEtlService
            .playWithFileUsingPOST(
                this.datastore,
                this.currentEtl.code,
                'null',
                false,
                null,
                this.file
            )
            .subscribe((res) => {
                //
            });
        this.tasksService.open();
        this.clearFormInputFile();
    }

    private clearFormInputFile() {
        const frm: HTMLFormElement = (
            document.getElementsByName('form')[0]
        ) as HTMLFormElement;
        frm.reset();
        this.form.reset();
        (document.getElementById('file_file') as HTMLInputElement).value = '';
        (document.getElementById('field_file') as HTMLInputElement).value = '';
        (document.getElementById('field_fileContentType') as HTMLInputElement).value = '';
    }

    delete(etl: EtlConf, event) {
        this.confirmationService.confirm({
            target: event.target,
            message: this.translate.instant('etl.new.confirm'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: this.translate.instant('primeng.accept'),
            rejectLabel: this.translate.instant('common.cancel'),
            accept: () => {
                this.configuratorEtlService.deleteEtlByIdUsingDELETE(this.datastore, etl.id).subscribe(() => {
                    this.loadAll();
                    this.messageService.add({ severity: 'success', summary: this.translate.instant('etl.list.deletedSummary') });
                }, (error) => {
                    const key = error?.status === 409
                        ? 'etl.error.not-removed-linked-campaign'
                        : 'etl.error.not-removed';
                    this.messageService.add({ severity: 'error', summary: this.translate.instant('etl.list.errorSummary'), detail: this.translate.instant(key) });
                });
            },
            reject: () => {
                // reject action
            }
        });
    }

    copy(name: string, description: string, partner: string) {
        this.copyDialog = false;
        const isAdmin = this.isSuperAdmin || this.isAuthorizedAdmin;
        this.initialPartner = null;
        // Copie faite par le serveur : l'écran ne reçoit plus les secrets de l'ETL (mots de passe FTP, clés
        // S3), il ne peut donc plus recopier l'ETL lu sans que la copie les perde.
        const duplicate: EtlDuplicateDTO = {
            code: name.toLowerCase().replace(/ /g, '').trim(),
            name,
            description: description ? description : '',
            owner: isAdmin ? 'admin' : partner,
            // Non renseigné pour un administrateur : la copie garde les affiliés de la source.
            personalDataAccessPartners: isAdmin ? undefined : (this.directionMode == 'IMPORT' ? [partner] : [])
        };
        this.configuratorEtlService.duplicateEtlUsingPOST(this.datastore, this.selectedEtlId, duplicate).subscribe(() => {
            this.loadAll();
            this.messageService.add({ severity: 'success', summary: this.translate.instant('etl.list.duplicatedSummary') });
        });
    }

    action(action: 'edit' | 'delete' | 'copy' | 'run' | 'export' | 'runScript' | 'logs' | 'runExport', etl: EtlConf, $event: any) {
        switch (action) {
            case 'edit':
                if (etl.extract == null && etl.processingLoad?.typeLoad === 'ScriptProcessingLoadConf') {
                    this.router.navigate([`/h/flux/${this.innerPath}/etl-script`, this.datastore, etl.id]);
                } else {
                    this.router.navigate([`/h/flux/${this.innerPath}/etl`, this.datastore, etl.id]);
                }
                break;
            case 'logs':
                this.router.navigate([`/h/flux/import-v1/task`],
                    {
                        queryParams: {
                            datastore: this.datastore,
                            etlIds: [etl.id]
                        }
                    });
                break;
            case 'delete':
                this.delete(etl, $event);
                break;
            case 'copy':
                this.selectedEtlId = etl.id;
                this.initialPartner = etl.partner;
                if (this.accessiblePartners.includes(etl.owner)) {
                    this.possiblePartners = this.accessiblePartners;
                }
                else {
                    this.possiblePartners = this.accessiblePartners.filter(partner =>
                        etl.affectedPartners.includes(partner)
                    );
                }
                if (this.initialPartner && this.possiblePartners.includes(this.initialPartner)) {
                    this.selectedPartner = this.initialPartner;
                } else if (this.possiblePartners.length > 0) {
                    this.selectedPartner = this.possiblePartners[0];
                }
                this.copyDialog = true;
                break;
            case 'run':
                this.runEtl(etl);
                this.dataFileInput.nativeElement.click();
                break;
            case 'export':
                this.exportJson(etl);
                break;
            case 'runScript':
                this.runScript(etl);
                break;
            case 'runExport':
                this.runExport(etl);
                break;
        }
    }

    buildItemsList(): void {
        this.itemsList = [
            {
                label: this.translate.instant('etl.list.addScript'),
                icon: 'fas fa-scroll',
                routerLink: `/h/flux/${this.innerPath}/etl/new-script`,
                permission: ['IMPORTS.newScript', 'EXPORTS.newScript']
            },
            {
                label: this.translate.instant('subscriptions.importFile'),
                icon: 'fa fa-upload',
                permission: ['IMPORTS.import', 'EXPORTS.import'],
                command: () => {
                    this.triggerFileUpload();
                }
            },
            {
                label: this.translate.instant('subscriptions.exportSelection'),
                icon: 'fas fa-file-export',
                permission: ['IMPORTS.export', 'EXPORTS.export'],
                command: () => {
                    this.exportZip('selected');
                }
            },
            {
                label: this.translate.instant('subscriptions.exportAll'),
                icon: 'fa fa-download',
                permission: ['IMPORTS.export', 'EXPORTS.export'],
                command: () => {
                    this.exportZip('all');
                }
            }
        ];
    }

    onCheckboxChange(event, etl: EtlConf): void {
        if (event.checked.length !== 0) {
            this.selectedEtls.push(etl);
        } else {
            this.selectedEtls = this.selectedEtls.filter((e) => e.code !== etl.code);
        }
    }

    triggerFileUpload(): void {
        this.etlFileInput.nativeElement.click();
    }

    resetFileInput(): void {
        this.etlFileInput.nativeElement.value = '';
    }

    // Cette méthode permet d'exporter un ETL en JSON, en faisant appel à l'API
    exportJson(etl: EtlConf): void {
        this.configuratorEtlService.exportEtl(etl.id, this.datastore).subscribe(
            (response) => {
                const a = document.createElement('a');
                a.href = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(response, null, 2));
                a.download = etl.code + '.json';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                this.messageService.add({ severity: 'success', summary: this.translate.instant('etl.list.exportSuccessSummary') });
            }, error => {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('etl.list.exportErrorSummary') });
        });
    }

    // Cette méthode permet d'exporter plusieurs ETLs en un seul fichier zip, en faisant appel à l'API
    handleZipExport(etlIds: string[]): void {
        this.configuratorEtlService.exportEtlInZip(etlIds, this.datastore).subscribe(
            (response) => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(new Blob([response], { type: 'application/zip' }));
                a.download = 'export.zip';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                this.messageService.add({ severity: 'success', summary: this.translate.instant('etl.list.exportSuccessSummary') });
            }, error => {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('etl.list.exportErrorSummary') });
            }
        );
    }

    // Cette méthode permet de définir les ZIP à exporter en fonction du mode de sélection
    exportZip(mode: string): void {
        switch (mode) {
            case 'selected':
                if (this.selectedEtls.length > 1) {
                    this.handleZipExport(this.selectedEtls.map((etl) => etl.id));
                } else {
                    this.messageService.add({ severity: 'warn', summary: this.translate.instant('etl.list.selectAtLeastTwoDetail') });
                }
                break;
            case 'all':
                if (this.etls.length > 0) {
                    this.handleZipExport(this.etls.map((etl) => etl.id));
                } else {
                    this.messageService.add({ severity: 'warn', summary: this.translate.instant('etl.list.noJobToExportDetail') });
                }
                break;
        }
    }

    // Cette méthode permet de vérifier le type de fichier importé et de déclencher le traitement adéquat
    importJson(event: any): void {
        const jsonFiles: FileList = event.target.files;
        if (jsonFiles.length === 0) {
            this.messageService.add({ severity: 'warn', summary: this.translate.instant('etl.list.noFileSelectedSummary') });
            return;
        }

        Array.from(jsonFiles).forEach((file: File) => {
            const reader: FileReader = new FileReader();
            reader.onload = (e: any) => {
                if (file.type === 'application/json') {
                    try {
                        const jsonContent: string = JSON.parse(e.target.result);
                        this.handleJsonImport(jsonContent);
                    } catch (error) {
                        this.messageService.add({ severity: 'error', summary: this.translate.instant('etl.list.jsonParseErrorSummary') });
                        this.resetFileInput();
                    }
                } else if (file.type === 'application/zip') {
                    this.handleZipImport(file);
                } else {
                    this.messageService.add({ severity: 'error', summary: this.translate.instant('etl.list.unsupportedFormatSummary') });
                    this.resetFileInput();
                }
            };
            reader.readAsText(file);
        });
    }

    // Cette méthode permet de gérer l'importation de fichiers JSON
    handleJsonImport(jsonContent: string): void {
        this.configuratorEtlService.importEtlFromJson(jsonContent, this.datastore).subscribe(
            (etl: any) => {
                this.loadAll();
                this.messageService.add({ severity: 'success', summary: this.translate.instant('etl.list.importSuccessSummary') });
                this.warnConnectionsToChoose([etl]);
                this.resetFileInput();
            }, error => {
                this.resetFileInput();
            }
        );
    }

    // Cette méthode permet de gérer l'importation de fichiers ZIP
    handleZipImport(zipFile: File): void {
        const formData: FormData = new FormData();
        formData.append('file', zipFile);
        this.configuratorEtlService.importEtlFromZip(formData, this.datastore).subscribe(
            (etls: any) => {
                this.loadAll();
                this.messageService.add({ severity: 'success', summary: this.translate.instant('etl.list.importSuccessSummary') });
                this.warnConnectionsToChoose(etls);
                this.resetFileInput();
            }, error => {
                this.resetFileInput();
            }
        );
    }

    /**
     * Un job importé arrive sans serveur de fichiers (connexion propre au tenant d'origine) et planification
     * suspendue : on liste ceux qui en utilisaient un, reconnaissables à leur nom de fichier ou à leurs canaux.
     */
    private warnConnectionsToChoose(etls: EtlConf[] | null): void {
        const names = (etls ?? []).filter(etl => {
            const extract = etl?.extract as SharedFileExtractConf;
            const stores = (etl?.processingLoad as FileProcessingLoadConf)?.stores ?? [];
            const importWithoutConnection = extract?.typeObject === 'SharedFileExtractConf' && !extract.connectionId && !!extract.fileName;
            const channelWithoutConnection = stores.some(store => !store.connectionId
                && (store.typeObject === 'FtpStoreConf' || store.typeObject === 'S3StoreConf'));
            return importWithoutConnection || channelWithoutConnection;
        }).map(etl => etl.name || etl.code);
        if (names.length > 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('etl.list.importConnectionSummary'),
                detail: this.translate.instant('etl.list.importConnectionDetail', {names: names.join(', ')}),
                sticky: true
            });
        }
    }

    private runScript(etl: EtlConf) {
        if (this.directionMode === 'IMPORT') {
            this.engineEtlService.runScriptImport(this.datastore, etl.id).subscribe(res => {
                        this.messageService.add({ severity: 'success', summary: this.translate.instant('etl.list.scriptDoneSummary'), detail: this.translate.instant('etl.list.resultDetail', {res}) });
                    });
        } else {
            this.engineEtlService.runScriptExport(this.datastore, etl.id).subscribe(res => {
                this.messageService.add({ severity: 'success', summary: this.translate.instant('etl.list.scriptDoneSummary'), detail: this.translate.instant('etl.list.resultDetail', {res}) });
            });
        }
    }

    private runExport(etl: EtlConf): void {
        this.engineEtlService.runScriptExportFile(this.datastore, etl.id, 'response').subscribe(response => {
            const blob = new Blob([response.body!], {type: response.headers.get('Content-Type') || 'application/octet-stream'});
            const filename = this.getFilenameFromContentDisposition(response.headers.get('Content-Disposition'));

            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = filename || 'fichier';
            link.click();

            // Libération de l'URL après téléchargement
            window.URL.revokeObjectURL(link.href);
        });
    }

    private getFilenameFromContentDisposition(contentDisposition: string | null): string | null {
        if (!contentDisposition) return null;
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        return matches && matches[1] ? matches[1].replace(/['"]/g, '') : null;
    }
    
    getButtonConfig(etl: EtlConf){
        const actionTypes = this.getAvailableActionsTypes(etl);
        if(actionTypes.length === 0){
            return { type: 'non'};
        } else if(actionTypes.length === 1){
            return {
                type: 'simple',
                action: actionTypes[0],
                etl:etl
            };
        } else {
            return {
                type: 'split',
                mainAction: actionTypes[0],
                dropdownActions: actionTypes.slice(1),
                etl:etl
            };
        }
    }

    private getAvailableActionsTypes(etl: EtlConf) {
        const actionTypes: any[] = [];

        if (this.isSuperAdmin || this.isAuthorizedAdmin || this.accessiblePartners?.includes(etl.owner)) {
            if (this.hasPermission(['IMPORTS.edit', 'EXPORTS.edit'])) {
                actionTypes.push({
                    type: 'edit',
                    label: this.translate.instant('etl.list.editAction'),
                    icon: "fas fa-pencil-alt"
                });
            }
            if (etl.processingLoad?.typeLoad === 'ScriptProcessingLoadConf' && this.hasPermission(['IMPORTS.runScript', 'EXPORTS.runScript'])){
                actionTypes.push({
                    type: 'runScript',
                    label: this.translate.instant('etl.list.runScriptAction'),
                    icon: 'fas fa-play',
                });
            } else if (etl.processingLoad?.typeLoad.includes('FileProcessingLoadConf') && this.hasPermission(['EXPORTS.run'])) {
                actionTypes.push({
                    type: 'runExport',
                    label: this.translate.instant('etl.list.runExportAction'),
                    icon: 'fas fa-play',
                });
            } else if (this.directionMode === 'IMPORT' && this.hasPermission(['IMPORTS.run'])) {
                actionTypes.push({
                    type: 'run',
                    label: this.translate.instant('subscriptions.importFile'),
                    icon: 'fas fa-play',
                });
            }
            if (this.hasPermission(['IMPORTS.read', 'EXPORTS.read'])) {
                actionTypes.push({
                    type: 'logs',
                    label: this.translate.instant('etl.list.logsAction'),
                    icon: 'fas fa-clipboard-list',
                });
            }
            if (this.hasPermission(['IMPORTS.copy', 'EXPORTS.copy'])) {
                actionTypes.push({
                    type: 'copy',
                    label: this.translate.instant('etl.list.duplicateAction'),
                    icon: 'fas fa-copy',
                });
            }
            if (this.hasPermission(['IMPORTS.delete', 'EXPORTS.delete'])) {
                actionTypes.push({
                    type: 'delete',
                    label: this.translate.instant('subscriptions.delete'),
                    icon: 'fas fa-trash',

                });
            }
        } else{
            if (this.hasPermission(['IMPORTS.copy', 'EXPORTS.copy'])) {
                actionTypes.push({
                    type: 'copy',
                    label: this.translate.instant('etl.list.duplicateAction'),
                    icon: 'fas fa-copy'
                });
            }
        }

        if (this.hasPermission(['IMPORTS.export', 'EXPORTS.export'])) {
                actionTypes.push({
                    type: 'export',
                    label: this.translate.instant('etl.list.exportConfigAction'),
                    icon: 'fas fa-file-export'
                });
            }
        
        return actionTypes;
    }

    private hasPermission(permissions: string[]): boolean {
        return permissions.some(permission => {
            const [page, action] = permission.split('.');
            return this.pagePermissions.hasPagePermission(page, action, true);
        });
    }

    buildDropdownActions(actionTypes: any[], etl: EtlConf, ev) {
        const actions: any = [];

        actionTypes.forEach(actionType => {
            actionType.command = (event) => this.action(actionType.type, etl, event.originalEvent);
            actions.push(actionType);
        });
        this.actionsList = actions;
    }

    hasActiveSchedule(etl: EtlConf): boolean {
        // Only show schedule icon if enableSchedule is true AND there are channels configured
        if (!etl?.extract?.enableSchedule) {
            return false;
        }

        // Check if there are any stores/channels configured
        const processingLoad = etl.processingLoad as any;
        if (processingLoad?.typeLoad === 'FileProcessingLoadConf') {
            return processingLoad.stores && processingLoad.stores.length > 0;
        }

        // For script-based exports, show schedule if enabled
        return true;
    }
}

